/**
 * Service layer — Scheduling (Gantt) domain
 *
 * Supabase masters milestone dates (EST/LFT/Duration); Airtable mirrors them.
 *  - seedFromMilestones: push the project's current Airtable values into Supabase on modal open
 *  - getProjectSchedule: read the stored slice back
 *  - saveScheduleDates: persist edits to Supabase (the master)
 *  - fetchCurrentAirtableDates: re-read Airtable for the close-time race check vs the nightly job
 *  - writeBackDates: batch-PATCH EST/LFT/Duration to Airtable (never the formula fields)
 */

import { apiPatchBatch, apiGetOne } from './_api.js'
import { getFirst } from '../outline/utils.js'
import { computeCascade, parseDate, dayDiff } from '../lib/cascade.js'

const TBL_DEL = 'tblFuiDpahqCL4eIK'  // Project Deliverables (milestones)

// Airtable field names (only these three are editable / written back)
const F_EST = 'Earliest Start (EST)'
const F_LFT = 'Latest Finish (LFT)'
const F_DUR = 'Duration'

/** Map an Airtable milestone record to a Supabase seed row. */
function toSeedRow(rec, idx) {
  const f = rec.fields || {}
  return {
    airtable_id:        rec.id,
    phase_id:           getFirst(f['Phase']) || null,
    // The deliverable-type bridge. Populated here (not only by the nightly importer) so a
    // milestone created since the last import is still bucketed by type — that link is what the
    // effort-days view joins on. Same source api/priority.js reads: the `Deliverable` link field.
    deliverable_type_airtable_id: getFirst(f['Deliverable']) || null,
    name:               f['Full Deliverable Name'] || f['Name'] || 'Milestone',
    earliest_start:     f[F_EST] ? String(f[F_EST]).slice(0, 10) : null,
    latest_finish:      f[F_LFT] ? String(f[F_LFT]).slice(0, 10) : null,
    duration_days:      (f[F_DUR] != null ? Number(f[F_DUR]) : null),
    date_pinned:        f['Date Pinned'] === true,
    // Milestone Behavior is an Airtable lookup (array) — store the scalar string, not the raw array,
    // so the text column holds 'Fixed'/'Reactive'/… (the cascade compares it as a scalar).
    milestone_behavior: getFirst(f['Milestone Behavior']) || null,
    sort_order:         (f[F_LFT] ? Date.parse(f[F_LFT]) : idx),  // chronological default; falls back to index
  }
}

/** Extract dependency edges from a milestone's Predecessor links. */
function toDepRows(rec) {
  const preds = rec.fields?.['Predecessor'] || []
  return preds.map(pid => ({ milestone_id: rec.id, predecessor_id: pid }))
}

async function post(body) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/schedule', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify(body),
  })
  if (!res.ok && res.status !== 207) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || 'Schedule request failed: ' + res.status)
  }
  return res.json()
}

/**
 * Seed (refresh) a project's scheduling slice into Supabase from already-loaded
 * Airtable milestone records, then return the stored rows.
 * @param {string} projectId
 * @param {Array} milestoneRecords  raw Airtable Project Deliverables records (e.g. ctx.state.milestones)
 * @returns {Promise<{milestones: Array, dependencies: Array}>}
 */
/**
 * Re-read these milestones from Airtable into the Supabase mirror, right now (cutover slice one,
 * 2026-09-03). Fire-and-forget by design: it is called right after an Airtable write (post,
 * unpost, checklist tick) so the Flight Board and the scheduler see the change without waiting
 * for the daily sync — but a mirror hiccup must never fail the write the user just made, so every
 * failure is swallowed (logged) here. Returns the server's count or null.
 */
export async function refreshMilestoneMirror(ids) {
  const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean)
  if (!list.length) return null
  try { return await post({ action: 'refresh', ids: list }) }
  catch (e) { console.warn('[schedule] mirror refresh skipped:', e && e.message); return null }
}

export async function seedFromMilestones(projectId, milestoneRecords) {
  const milestones  = milestoneRecords.map(toSeedRow)
  const dependencies = milestoneRecords.flatMap(toDepRows)
  return post({ action: 'seed', projectId, milestones, dependencies })
}

/**
 * Recompute + return this project's buffered timeline (chain + project buffer, sheets 5+6 of
 * docs/RULES_OF_FLOW.md). Fails open — a Gantt view must never break because this couldn't load.
 * @returns {Promise<object|null>} the project_timelines row, or null if unavailable / not applicable
 *   (e.g. no Construction Kick-Off milestone yet).
 */
export async function getProjectTimeline(projectId) {
  try {
    const r = await post({ action: 'timeline', projectId })
    return (r && r.timeline) || null
  } catch (e) { console.warn('[schedule] timeline unavailable:', e && e.message); return null }
}

/** Read a project's stored scheduling slice from Supabase. */
export async function getProjectSchedule(projectId) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/schedule?projectId=' + encodeURIComponent(projectId), {
    headers: { 'Authorization': 'Bearer ' + token },
  })
  if (!res.ok) return { milestones: [], dependencies: [] }
  return res.json()
}

/**
 * Persist edited dates to Supabase (the master).
 * @param {Array} updates  [{ airtable_id, earliest_start, latest_finish, duration_days, pending_airtable_sync? }]
 */
export async function saveScheduleDates(updates) {
  return post({ action: 'save', updates })
}

/**
 * Re-read current Airtable EST/LFT/Duration for the given record ids.
 * Used at modal close to detect a nightly-job write underneath the open modal.
 * @returns {Promise<Map<string,{est:string,lft:string,duration:number}>>}
 */
export async function fetchCurrentAirtableDates(ids) {
  const map = new Map()
  await Promise.all(ids.map(async id => {
    const rec = await apiGetOne(TBL_DEL, id)
    if (rec) {
      const f = rec.fields || {}
      map.set(id, {
        est:      f[F_EST] ? String(f[F_EST]).slice(0, 10) : null,
        lft:      f[F_LFT] ? String(f[F_LFT]).slice(0, 10) : null,
        duration: (f[F_DUR] != null ? Number(f[F_DUR]) : null),
      })
    }
  }))
  return map
}

/**
 * Batch-write EST/LFT/Duration back to Airtable. Never writes formula fields.
 * @param {Array} changes  [{ airtable_id, earliest_start, latest_finish, duration_days }]
 * @returns {Promise<{ succeeded: string[], failed: {id,error}[] }>}
 */
export async function writeBackDates(changes) {
  const records = changes.map(c => {
    const fields = {}
    if (c.earliest_start !== undefined) fields[F_EST] = c.earliest_start || null
    if (c.latest_finish  !== undefined) fields[F_LFT] = c.latest_finish || null
    if (c.duration_days  !== undefined) fields[F_DUR] = (c.duration_days ?? null)
    if (c.date_pinned    !== undefined) fields['Date Pinned'] = !!c.date_pinned
    return { id: c.airtable_id, fields }
  })
  return apiPatchBatch(TBL_DEL, records)
}

// ── Dependency editing (Gantt) ───────────────────────────────────────────────
// The Predecessor link on the milestone is the source of truth; schedule_dependencies mirrors it.

/** Replace the project's dependency edges in Supabase. `deps`: [{ milestone_id, predecessor_id }]. */
export async function saveDependencies(projectId, deps) {
  return post({ action: 'replaceDeps', projectId, dependencies: deps })
}

/** Write each changed milestone's full Predecessor link list back to Airtable.
 *  `items`: [{ airtable_id, predecessor_ids: string[] }].
 *
 * Also refreshes each touched milestone's mirror row (same fire-and-forget contract as
 * refreshMilestoneMirror's other callers) — the Flight Board's "blocked" check reads
 * schedule_milestones.predecessor_airtable_id, not Airtable directly, so without this the mirror
 * kept whatever predecessor it last saw until the next nightly sync. That's what let a Gantt
 * dependency delete look like it "didn't stick": Airtable had it right, the mirror didn't.
 */
export async function writeBackPredecessors(items) {
  const records = items.map(i => ({ id: i.airtable_id, fields: { Predecessor: i.predecessor_ids || [] } }))
  const result = await apiPatchBatch(TBL_DEL, records)
  refreshMilestoneMirror(items.map(i => i.airtable_id))
  return result
}

// ── Posted-date → schedule sync (post modal) ─────────────────────────────────
// When a milestone is posted to a date that differs from its LFT and the user opts to sync the
// schedule, we move the LFT to the posted date and re-flow downstream — the same cascade the Gantt
// and nightly use. Reuses computeCascade so the previewed impact and the committed change match.

function cascadeInputs(records) {
  const milestones = (records || []).map(r => {
    const f = r.fields || {}
    return {
      id: r.id,
      earliestStart: f[F_EST] ? String(f[F_EST]).slice(0, 10) : null,
      latestFinish:  f[F_LFT] ? String(f[F_LFT]).slice(0, 10) : null,
      datePinned:    f['Date Pinned'] === true,
      behavior:      f['Milestone Behavior'] || null,
    }
  })
  const dependencies = (records || []).flatMap(r => (r.fields?.['Predecessor'] || []).map(pid => ({ milestoneId: r.id, predecessorId: pid })))
  return { milestones, dependencies }
}

/**
 * Preview the downstream re-flow if `msId`'s finish moves to `newFinish`. Pure — no I/O.
 * @returns {{ deltaDays:number, downstream:[{id,name,oldFinish,latestFinish,earliestStart,deltaDays}] }}
 *   deltaDays on the root: + = posted later than LFT (pushes work back), − = earlier (recovers time).
 */
export function previewFinishCascade(msId, newFinish, records) {
  const { milestones, dependencies } = cascadeInputs(records)
  const updates = computeCascade({ rootId: msId, rootFinish: newFinish, milestones, dependencies })
  const byId = new Map((records || []).map(r => [r.id, r]))
  const root = byId.get(msId)
  const rootOldLft = root?.fields?.[F_LFT] ? String(root.fields[F_LFT]).slice(0, 10) : null
  const rootDelta = (rootOldLft && newFinish) ? dayDiff(parseDate(rootOldLft), parseDate(newFinish)) : 0
  const downstream = []
  updates.forEach((u, id) => {
    const f = byId.get(id)?.fields || {}
    const oldF = f[F_LFT] ? String(f[F_LFT]).slice(0, 10) : null
    downstream.push({
      id, name: f['Full Deliverable Name'] || f['Name'] || 'Milestone',
      oldFinish: oldF, latestFinish: u.latestFinish, earliestStart: u.earliestStart,
      deltaDays: (oldF && u.latestFinish) ? dayDiff(parseDate(oldF), parseDate(u.latestFinish)) : 0,
    })
  })
  return { deltaDays: rootDelta, downstream }
}

/**
 * Commit a posted milestone's finish change to `newFinish` plus its downstream cascade.
 * Supabase masters dates (seed → save), Airtable mirrors (batch write-back). Updates `records` in place.
 * @returns {Promise<{ changed:number, failed:Array }>}
 */
export async function syncFinishAndCascade({ projectId, msId, newFinish, records }) {
  const { milestones, dependencies } = cascadeInputs(records)
  const updates = computeCascade({ rootId: msId, rootFinish: newFinish, milestones, dependencies })

  const changes = [{ airtable_id: msId, latest_finish: newFinish }]   // root: move LFT only (EST/Duration untouched)
  updates.forEach((u, id) => changes.push({ airtable_id: id, earliest_start: u.earliestStart, latest_finish: u.latestFinish }))

  // Supabase master first (seed so rows exist, then save), then Airtable mirror.
  try { if (projectId) await seedFromMilestones(projectId, records) } catch { /* mirror seed best-effort */ }
  try { await saveScheduleDates(changes) } catch { /* master save best-effort; Airtable is authoritative for un-migrated */ }
  const wb = await writeBackDates(changes)

  // Reflect in the in-memory milestone cache so the outline updates without a reload.
  changes.forEach(c => {
    const r = (records || []).find(x => x.id === c.airtable_id)
    if (!r) return
    if (c.earliest_start !== undefined) r.fields[F_EST] = c.earliest_start
    if (c.latest_finish !== undefined) r.fields[F_LFT] = c.latest_finish
  })
  return { changed: changes.length, failed: (wb && wb.failed) || [] }
}
